import type { KeyboardEvent, MouseEvent, PointerEvent } from "react";
import { Copy } from "lucide-react";
import { toast } from "react-toastify";
import { Tooltip } from "@/components/atoms/Tooltip";
import { copyTextToClipboard } from "@/lib/clipboard";
import { cn } from "@/lib/cn";

interface CopyButtonProps {
  value: string | number | null | undefined;
  label: string;
  successMessage?: string;
  errorMessage?: string;
  className?: string;
}

export function CopyButton({
  value,
  label,
  successMessage = `${label} copied to clipboard`,
  errorMessage = `Unable to copy ${label.toLowerCase()}`,
  className,
}: CopyButtonProps) {
  const copyValue = typeof value === "number" ? String(value) : value;
  if (typeof copyValue !== "string" || !copyValue.trim()) return null;

  const stopPropagation = (event: PointerEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  };

  const handleCopy = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await copyTextToClipboard(copyValue);
      toast.success(successMessage);
    } catch {
      toast.error(errorMessage);
    }
  };

  return (
    <Tooltip content={`Copy ${label.toLowerCase()}`} side="top">
      <button
        type="button"
        aria-label={`Copy ${label.toLowerCase()}`}
        onPointerDown={stopPropagation}
        onKeyDown={stopPropagation}
        onClick={(event) => void handleCopy(event)}
        className={cn(
          "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-bridge-muted opacity-0 transition-[color,background-color,opacity] [@media(hover:none)]:opacity-100 hover:bg-bridge-border/50 hover:text-bridge-fg focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bridge-accent group-hover/copy:opacity-100 group-focus-within/copy:opacity-100",
          className,
        )}
      >
        <Copy size={14} aria-hidden="true" />
      </button>
    </Tooltip>
  );
}
